<script context="module">
  import { groupBy, prop, pluck } from "ramda";
  import { tick } from "svelte";
  import { writable, get } from "svelte/store";

  const notifications = writable([]);

  let notificationsIndex = [];

  let queueLimit = 15;
  let notificationTTL = 5000;
  let schedulerId;

  /**
   * A notification
   * @typedef {Object} Notification
   * @property {string} [id] - The id
   * @property {string} [title] - The title
   * @property {string} [description] - The description
   * @property {number} [progress] - The progress
   * @property {NotificationType} [type="info"] - The type
   */

  /**
   * Add notification to notification center
   * @param {Notification} notification - The {@link Notification} to be added
   * @return {void}
   */

  export async function addNotification(inputNotification) {
    let notification = { ...inputNotification };

    notification.removeAt =
      Date.now() + (notification.duration || notificationTTL);
    let index = notificationsIndex.indexOf(notification.id);
    if (index > -1) {
      notificationsIndex.splice(index, 1);
      notificationsIndex.unshift(notification.id);

      notifications.update((prev) => {
        prev.splice(index, 1);
        prev.unshift(notification);
        return prev;
      });
    } else {
      notificationsIndex.unshift(notification.id);
      notifications.update((prev) => {
        prev.unshift(notification);
        return prev;
      });
    }
    if (notificationsIndex.length > queueLimit) {
      notificationsIndex.length = queueLimit;
      notifications.update((prev) => {
        prev.length = queueLimit;
        return prev;
      });
    }
    startScheduler();
    notificationsIndex = notificationsIndex;
    await tick();
  }

  export function show(description, params = {}) {
    addNotification(
      Object.assign(
        {
          id: Date.now(),
          description,
        },
        params
      )
    );
  }

  function removeNotifications(ids) {
    ids.forEach((id) => {
      let index = notificationsIndex.indexOf(id);
      let notification = get(notifications)[index];
      if (index > -1) {
        notifications.update((prev) => {
          prev.splice(index, 1);
          return prev;
        });
        notificationsIndex.splice(index, 1);
      }
    });
    if (notifications.length === 0) {
      stopScheduler();
    }
    notificationsIndex = notificationsIndex;
  }

  export function closeAllNotifications() {
    removeNotifications(pluck("id", get(notifications)));
  }

  function startScheduler() {
    if (schedulerId) return;
    schedulerId = setInterval(async () => {
      if (notifications.length === 0) return;
      await tick();
      const ids = get(notifications)
        .filter((notification) => {
          if (notification.removeAt <= Date.now()) {
            return true;
          }
        })
        .map(prop("id"));
      removeNotifications(ids);
    }, notificationTTL);
  }

  function stopScheduler() {
    if (schedulerId) clearInterval(schedulerId);
    schedulerId = undefined;
  }
</script>

<script>
  import { flip } from "svelte/animate";
  import { fly } from "svelte/transition";

  import Portal from "svelte-portal";
  import Notification from "./Notification";

  export let topoffset = 65;

  let windowInnerHeight = 0;

  function closeNotification(notification) {
    removeNotifications([notification.id]);
  }
</script>

<style>
  .notifications {
    position: fixed;
    right: 0;
    padding: 0 20px;
    width: 400px;
    overflow: auto;
    max-height: 100%;
    pointer-events: none;
  }
</style>

{#if $notifications.length > 0}
  <Portal>
    <div
      class="notifications z-30"
      style="top:{topoffset}px; height:{windowInnerHeight - topoffset}px;">
      {#each $notifications as notification (notification.id)}
        <div animate:flip in:fly={{ y: -100, duration: 300 }}>
          <Notification
            {...notification}
            on:closeNotification={() => closeNotification(notification)} />
        </div>
      {/each}
    </div>
  </Portal>
{/if}

<svelte:window bind:innerHeight={windowInnerHeight} />
